import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button, Tag, message, Spin, Badge, Input, Modal } from 'antd';
import {
  CameraOutlined, CheckCircleFilled, CloseCircleFilled,
  CloudUploadOutlined, FileImageOutlined, DeleteOutlined,
  FolderOutlined, MobileOutlined, ReloadOutlined,
  DisconnectOutlined, LoadingOutlined, PlusOutlined,
  SearchOutlined, FireOutlined, CarOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://pts-pettycash-api.onrender.com';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const MobileScanPage = () => {
  const pathParts = window.location.pathname.split('/');
  const sessionId = pathParts[pathParts.length - 1] || pathParts[pathParts.length - 2];

  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ── Fuel Mode State ──
  const [isFuelMode, setIsFuelMode] = useState(false);
  const [fuelNames, setFuelNames] = useState([]);
  const [fuelSearchQuery, setFuelSearchQuery] = useState('');
  const [fuelModalOpen, setFuelModalOpen] = useState(false);
  const [pendingPhotoId, setPendingPhotoId] = useState(null);
  const [addingNewFuel, setAddingNewFuel] = useState(false);
  const [newFuelName, setNewFuelName] = useState('');
  const [nextSerial, setNextSerial] = useState(56);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const fuelSearchRef = useRef(null);

  // ─── Fetch session info ───
  const fetchSession = useCallback(async () => {
    if (!sessionId || sessionId.length < 5) {
      setExpired(true);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/scan-session/${sessionId}/mobile`);
      if (response.data.success) {
        setSessionInfo(response.data);
        // Check if fuel mode from session metadata
        if (response.data.is_fuel_mode) {
          setIsFuelMode(true);
          fetchFuelNames();
        }
        if (response.data.remaining_seconds <= 0) {
          setExpired(true);
        }
      } else {
        setExpired(true);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setExpired(true);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // ─── Fetch fuel names ───
  const fetchFuelNames = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fuel-names`);
      if (response.data.success) {
        setFuelNames(response.data.fuel_names || []);
      }
    } catch (err) {
      console.error('Failed to fetch fuel names:', err);
    }
  };

  const fetchNextSerial = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fuel-names/next-serial`);
      if (response.data.success) {
        setNextSerial(response.data.next_serial);
      }
    } catch { /* ignore */ }
  };

  // ─── Search fuel names ───
  const searchFuelNames = async (query) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fuel-names/search?q=${encodeURIComponent(query)}`);
      if (response.data.success) {
        setFuelNames(response.data.fuel_names || []);
      }
    } catch { /* ignore */ }
  };

  // ─── Upload a photo ───
  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/scan-session/${sessionId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
          onUploadProgress: (event) => {
            if (event.total) setUploadProgress(Math.round((event.loaded / event.total) * 100));
          },
        }
      );

      if (response.data.success) {
        const photoData = {
          photo_id: response.data.photo_id,
          filename: response.data.filename,
          size: response.data.size,
          fuel_serial: null,
          fuel_name: null,
        };

        if (navigator.vibrate) navigator.vibrate(100);

        // If fuel mode, show fuel name selector modal
        if (isFuelMode) {
          setPendingPhotoId(response.data.photo_id);
          setUploadedPhotos(prev => [...prev, photoData]);
          setFuelSearchQuery('');
          await fetchFuelNames(); // Refresh list
          setFuelModalOpen(true);
          setTimeout(() => fuelSearchRef.current?.focus(), 300);
        } else {
          setUploadedPhotos(prev => [...prev, photoData]);
        }
      } else {
        message.error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setExpired(true);
        message.error('Session has expired');
      } else {
        message.error('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ─── Assign fuel name to photo ───
  const assignFuelName = (serialNo, fuelName) => {
  if (!pendingPhotoId) return;

  setUploadedPhotos(prev =>
    prev.map(p =>
      p.photo_id === pendingPhotoId
        ? { ...p, fuel_serial: serialNo, fuel_name: fuelName }
        : p
    )
  );

  // Notify backend
  axios.post(`${API_BASE_URL}/api/scan-session/${sessionId}/assign-fuel`, {
    photo_id: pendingPhotoId,
    fuel_serial: serialNo,      // ← This matches backend
    fuel_name: fuelName,        // ← This matches backend
  }).catch(() => { /* non-critical */ });

  setFuelModalOpen(false);
  setPendingPhotoId(null);
  setFuelSearchQuery('');
};

  // ─── Add new fuel name ───
  const handleAddNewFuel = async () => {
    if (!newFuelName.trim()) {
      message.warning('Enter a fuel name');
      return;
    }
    setAddingNewFuel(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/fuel-names`, {
        fuel_name: newFuelName.trim(),
        created_by: sessionInfo?.username || 'mobile',
      });
      if (response.data.success) {
        const { serial_no, fuel_name } = response.data;
        message.success(`Created #${serial_no}: ${fuel_name}`);
        // Auto-assign to pending photo
        assignFuelName(serial_no, fuel_name);
        setNewFuelName('');
        fetchFuelNames();
        fetchNextSerial();
      } else {
        message.error(response.data.message || 'Failed to create');
      }
    } catch (err) {
      message.error('Failed to create fuel name');
    } finally {
      setAddingNewFuel(false);
    }
  };

  // ─── Handle file selection ───
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      await uploadPhoto(file);
    }
    if (e.target) e.target.value = '';
  };

  // ─── Delete photo ───
  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/scan-session/${sessionId}/photo/${photoId}`);
      setUploadedPhotos(prev => prev.filter(p => p.photo_id !== photoId));
    } catch {
      message.error('Failed to remove');
    }
  };

  // ─── Filtered fuel names ───
  const filteredFuelNames = fuelSearchQuery.trim()
    ? fuelNames.filter(f => {
        const q = fuelSearchQuery.toLowerCase();
        return (
          String(f.serial_no).includes(q) ||
          f.fuel_name.toLowerCase().includes(q)
        );
      })
    : fuelNames;

  // ═══════════════════════════════════════════
  //  RENDER: Loading
  // ═══════════════════════════════════════════

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.centeredContent}>
          <Spin size="large" />
          <Text style={{ color: '#5a6d80', marginTop: 16, fontSize: 14 }}>Connecting to scan session...</Text>
        </div>
      </div>
    );
  }

  if (expired || !sessionInfo) {
    return (
      <div style={styles.container}>
        <div style={styles.centeredContent}>
          <div style={{ width: 72, height: 72, borderRadius: 18, background: 'linear-gradient(135deg, #fff2f0, #ffccc7)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <DisconnectOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
          </div>
          <Title level={4} style={{ color: '#1a3353', margin: '0 0 8px' }}>Session Expired</Title>
          <Text style={{ color: '#5a6d80', fontSize: 14, textAlign: 'center', display: 'block', maxWidth: 300 }}>
            This scan session has expired. Please scan a new QR code from the desktop portal.
          </Text>
          <Button icon={<ReloadOutlined />} onClick={fetchSession} style={{ marginTop: 20, borderRadius: 10 }}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  //  RENDER: Active Session
  // ═══════════════════════════════════════════

  return (
    <div style={styles.container}>
      {/* Hidden file inputs */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{ display: 'none' }} />
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} style={{ display: 'none' }} />

      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/tab.png" alt="PTS" style={{ width: 32, height: 32, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <Text strong style={{ color: '#fff', fontSize: 14, display: 'block' }}>PTS Scanner</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>Phoenician Technical Services</Text>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isFuelMode && (
            <Tag style={{ background: 'linear-gradient(135deg, #fa541c, #ff7a45)', border: 'none', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
              <FireOutlined style={{ marginRight: 4 }} /> FUEL
            </Tag>
          )}
          <Badge count={uploadedPhotos.length} style={{ backgroundColor: '#52c41a' }}>
            <Tag style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: 6, fontSize: 11 }}>
              <CameraOutlined style={{ marginRight: 4 }} /> {uploadedPhotos.length}
            </Tag>
          </Badge>
        </div>
      </div>

      {/* Session info */}
      <div style={styles.sessionInfo}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FolderOutlined style={{ color: '#1a6ed8' }} />
          <Text strong style={{ color: '#1a3353' }}>Folder {sessionInfo.folder_number}</Text>
          <Tag color={sessionInfo.module_type === 'site-petty-cash' ? 'processing' : 'warning'} style={{ borderRadius: 4, fontSize: 10 }}>
            {sessionInfo.module_label}
          </Tag>
          {isFuelMode && (
            <Tag color="volcano" style={{ borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
              <FireOutlined /> Fuel Mode
            </Tag>
          )}
        </div>
      </div>

      {/* Main scan area */}
      <div style={styles.scanArea}>
        {uploading ? (
          <div style={styles.uploadingState}>
            <LoadingOutlined style={{ fontSize: 40, color: '#1a6ed8' }} spin />
            <Text style={{ color: '#1a6ed8', fontSize: 16, fontWeight: 600, marginTop: 16 }}>
              Uploading... {uploadProgress}%
            </Text>
            <div style={{ width: '80%', height: 6, background: '#e8ecf1', borderRadius: 3, marginTop: 12, overflow: 'hidden' }}>
              <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'linear-gradient(90deg, #1a6ed8, #4a9eff)', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
          </div>
        ) : (
          <div>
            <div onClick={() => cameraInputRef.current?.click()} style={styles.cameraButton}>
              <CameraOutlined style={{ fontSize: 48, color: '#fff' }} />
              <Text style={{ color: '#fff', fontSize: 16, fontWeight: 700, marginTop: 8 }}>Take Photo</Text>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Tap to open camera</Text>
            </div>
            <Button icon={<FileImageOutlined />} onClick={() => fileInputRef.current?.click()} block size="large"
              style={{ marginTop: 16, borderRadius: 12, height: 50, fontWeight: 600, fontSize: 14, borderColor: '#d6e4f0' }}>
              Choose from Gallery
            </Button>
          </div>
        )}
      </div>

      {/* Uploaded photos list */}
      {uploadedPhotos.length > 0 && (
        <div style={styles.photosList}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 14, color: '#1a3353' }}>Uploaded ({uploadedPhotos.length})</Text>
            <Tag color="success" style={{ borderRadius: 4 }}>
              <CheckCircleFilled style={{ marginRight: 4 }} /> Sent to desktop
            </Tag>
          </div>                 

          

          {uploadedPhotos.map((photo, i) => (
            <div key={photo.photo_id} style={styles.photoItem}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileImageOutlined style={{ color: '#1a6ed8', fontSize: 16 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13, color: '#1a3353', display: 'block' }}>
                    Photo #{i + 1}
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <Text style={{ fontSize: 11, color: '#8dabc4' }}>{formatFileSize(photo.size)}</Text>
                    {/* Fuel name tag */}
                    {isFuelMode && photo.fuel_serial && (
                      <Tag color="volcano" style={{ borderRadius: 4, fontSize: 10, margin: 0 }}>
                        #{photo.fuel_serial} {photo.fuel_name}
                      </Tag>
                    )}
                    {isFuelMode && !photo.fuel_serial && (
                      <Tag
                        color="warning"
                        style={{ borderRadius: 4, fontSize: 10, margin: 0, cursor: 'pointer' }}
                        onClick={() => {
                          setPendingPhotoId(photo.photo_id);
                          setFuelSearchQuery('');
                          fetchFuelNames();
                          setFuelModalOpen(true);
                        }}
                      >
                        ⚠ Assign fuel name
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
            </div>
          ))}
        </div>
      )}

      {/* Footer hint */}
      <div style={styles.footer}>
        <Text style={{ color: '#8dabc4', fontSize: 12, textAlign: 'center', display: 'block' }}>
          Photos are sent to the desktop portal in real-time.
          {isFuelMode && <><br />Select a fuel name for each photo after uploading.</>}
          <br />Process them from the desktop when you're done scanning.
        </Text>
      </div>

      {/* ═══════════════════════════════════════════
           FUEL NAME SELECTOR MODAL
         ═══════════════════════════════════════════ */}
      <Modal  
        open={fuelModalOpen}
        onCancel={() => { setFuelModalOpen(false); setPendingPhotoId(null); }}
        footer={null}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FireOutlined style={{ color: '#fa541c' }} />
            <span style={{ fontWeight: 700, color: '#1a3353' }}>Select Fuel Name</span>
          </div>
        }
        width="95%"
        style={{ top: 10, maxWidth: 500 }}
        styles={{ body: { padding: '12px 16px', maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Search input */}
        <Input
          ref={fuelSearchRef}
          placeholder="Search by number or name... (e.g., 4 or Manoj)"
          prefix={<SearchOutlined style={{ color: '#8dabc4' }} />}
          value={fuelSearchQuery}
          onChange={(e) => setFuelSearchQuery(e.target.value)}
          onPressEnter={() => {
            // If search is numeric and matches exactly one, auto-select
            if (filteredFuelNames.length === 1) {
              const f = filteredFuelNames[0];
              assignFuelName(f.serial_no, f.fuel_name);
            }
          }}
          size="large"
          style={{ borderRadius: 10, marginBottom: 10 }}
          allowClear
          autoFocus
        />

        {/* Fuel names list */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)', border: '1px solid #e8ecf1', borderRadius: 10 }}>
          {filteredFuelNames.map((f) => (
            <div
              key={f.serial_no}
              onClick={() => assignFuelName(f.serial_no, f.fuel_name)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px',
                borderBottom: '1px solid #f0f3f6',
                cursor: 'pointer',
                transition: 'background 0.1s',
                WebkitTapHighlightColor: 'rgba(26,110,216,0.1)',
              }}
              onTouchStart={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
              onTouchEnd={(e) => { e.currentTarget.style.background = 'transparent'; }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'linear-gradient(135deg, #fa541c, #ff7a45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 13, flexShrink: 0,
              }}>
                {f.serial_no}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: 14, color: '#1a3353', fontWeight: 600, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {f.fuel_name}
                </Text>
                {!f.is_default && (
                  <Text style={{ fontSize: 10, color: '#8dabc4' }}>Custom • by {f.created_by}</Text>
                )}
              </div>
              <CarOutlined style={{ color: '#8dabc4', fontSize: 14 }} />
            </div>
          ))}

          {filteredFuelNames.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center' }}>
              <Text style={{ color: '#8dabc4', fontSize: 13 }}>No matches found</Text>
            </div>
          )}
        </div>

        {/* Add new fuel name section */}
        <div style={{
          marginTop: 10, padding: '12px 14px',
          background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 10,
        }}>
          <Text strong style={{ fontSize: 12, color: '#531dab', display: 'block', marginBottom: 8 }}>
            <PlusOutlined style={{ marginRight: 4 }} /> Create New (Serial #{nextSerial})
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              placeholder="e.g., Fuel NewDriver_12345"
              value={newFuelName}
              onChange={(e) => setNewFuelName(e.target.value)}
              onPressEnter={handleAddNewFuel}
              style={{ flex: 1, borderRadius: 8 }}
              onFocus={fetchNextSerial}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNewFuel}
              loading={addingNewFuel}
              style={{ borderRadius: 8, background: '#722ed1', borderColor: '#722ed1' }}
            >
              Add
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════════

const styles = {
  container: { minHeight: '100vh', background: '#f4f7fa', maxWidth: 480, margin: '0 auto', position: 'relative' },
  centeredContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 24 },
  header: { background: 'linear-gradient(135deg, #1a3353, #1a6ed8)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 },
  sessionInfo: { background: '#fff', padding: '12px 20px', borderBottom: '1px solid #e8ecf1' },
  scanArea: { padding: '24px 20px' },
  cameraButton: { width: '100%', height: 200, borderRadius: 20, background: 'linear-gradient(135deg, #1a6ed8, #4a9eff)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 8px 32px rgba(26,110,216,0.3)', transition: 'transform 0.15s', WebkitTapHighlightColor: 'transparent', userSelect: 'none' },
  uploadingState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' },
  photosList: { padding: '0 20px 20px' },
  photoItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid #e8ecf1', marginBottom: 8 },
  footer: { padding: '20px', borderTop: '1px solid #e8ecf1', background: '#fff' },
};

export default MobileScanPage;